/**
 * LE JOURNAL VU COMME UNE SEMAINE : les conversions, et rien d'autre.
 *
 * POURQUOI UN MODULE A PART, ET POURQUOI IL EST PUR. La grille de Ma semaine
 * raisonne en JOUR DE LA SEMAINE et en HEURES DECIMALES (lundi vaut 0, 14:30
 * vaut 14.5) parce qu'une grille se dessine comme ca ; le journal, lui, ne
 * connait qu'un INSTANT (`startAt`) et des MINUTES (`durationMinutes`). Tant
 * que la traduction vit dans le composant, elle se verifie a l'oeil, dans un
 * navigateur, une semaine a la fois. Ici elle n'importe ni React ni le SDK :
 * elle se lit d'un bout a l'autre, et elle se testerait sans monter un ecran.
 *
 * CE QU'UNE FICHE ABIMEE PRODUIT : `null`, jamais une exception. Le socle peut
 * projeter des pseudo-fiches (une fiche vide en cours de creation, un document
 * ancien sans `startAt`), et une surface canvas n'a aucune vue de repli derriere
 * elle : lever ici ferait tomber la semaine entiere sur le filet du socle pour
 * une seule ligne bancale. Elles sont donc ECARTEES, en silence.
 *
 * SON SEUL IMPORT EST UN TYPE, donc rien a l'execution (`verbatimModuleSyntax`
 * l'efface) : le sac de champs sur-mesure n'accepte pas n'importe quelle valeur
 * (`customFieldValueSchema`, `@taki/data-model`), et recopier cette union ici en
 * ferait une seconde verite qui divergerait au premier type ajoute.
 *
 * LE TEMPS EST LOCAL, ET C'EST VOULU. `startAt` est un instant absolu ; la
 * grille, elle, montre l'heure du POSTE. Toutes les lectures passent donc par
 * `getHours()` / `getDate()` (heure locale), jamais par les accesseurs UTC : un
 * creneau pose a 14:00 se relit a 14:00 sur le meme poste, et un changement
 * d'heure ne decale pas la colonne du jour (le calcul de jour arrondit, un jour
 * de bascule durant 23 ou 25 heures).
 */

import type { CustomFieldValue } from '@taki/data-model';

/** Le pas de saisie, partout : cinq minutes, jamais une seconde de plus fin. */
export const PAS_MINUTES = 5;

/** Une journee en millisecondes, pour le calcul de colonne. */
const JOUR_MS = 86_400_000;

/** Un creneau pose sur la grille de la semaine. */
export interface CreneauSemaine {
  /** L'id de la fiche du journal : c'est elle que le SDK ecrit. */
  id: string;
  /** La colonne : lundi vaut 0, dimanche vaut 6. */
  day: number;
  /** L'heure locale de debut, en heures decimales (14:30 vaut 14.5). */
  start: number;
  /** La duree AFFICHEE, en heures, deja coupee a minuit. */
  duration: number;
  /**
   * Le titre du creneau : le mot du champ a choix qui titre une saisie quand
   * une org en declare un (`titreDuJeu`) ; « Saisie » sur le modele, que Ma
   * semaine titre par le nom de la tache (`titreDe`).
   */
  title: string;
  /** Le projet designe, absent quand le creneau n'en porte pas. */
  projectId?: string;
  /** La tache designee (une tache de Projets, famille `tasks`), absente sinon. */
  taskId?: string;
  /** La personne dont c'est l'agenda : son `personId` (`workerId`), celui de l'annuaire. */
  ownerId: string;
  /** La note libre (`customFields.notes`), absente quand elle est vide. */
  note?: string;
  /**
   * LES CHOIX POSES sur le creneau, par clef de champ : le sac tel quel, filtre
   * aux clefs que le jeu declare a choix. La grille ne nomme aucune de ces
   * clefs (`categorie` chez une agence, aucune sur le modele).
   */
  choix?: Record<string, string>;
}

/**
 * CE QUE LA SURFACE REECRIT POUR DEFAIRE UN GESTE : l'etat d'une fiche AVANT
 * qu'on y touche. Volontairement partiel, et sans `null` nulle part : un champ
 * natif absent reste absent, il ne se vide pas (voir `photoDeFiche`).
 */
export interface PhotoDeFiche {
  startAt?: Date;
  durationMinutes?: number;
  /** `null` EFFACE (le SDK le permet pour le projet et la tache d'une saisie). */
  projectId?: string | null;
  taskId?: string | null;
  customFields?: Record<string, CustomFieldValue>;
}

/* ------------------------------------------------------------------ */
/* Lire une fiche generique sans jamais lui faire confiance             */
/* ------------------------------------------------------------------ */

/** Une chaine non vide, ou `undefined` : tout le reste ne vaut rien ici. */
function texte(valeur: unknown): string | undefined {
  if (typeof valeur !== 'string') return undefined;
  const propre = valeur.trim();
  return propre === '' ? undefined : propre;
}

/** Le sac de champs sur-mesure de la fiche, vide plutot qu'absent. */
function sacDe(fiche: Record<string, unknown>): Record<string, unknown> {
  const sac = fiche['customFields'];
  return typeof sac === 'object' && sac !== null ? (sac as Record<string, unknown>) : {};
}

/**
 * Une valeur que le sac accepte VRAIMENT. Tout le reste (un objet, une Date,
 * un tableau melange) est ignore : le schema du socle le refuserait a
 * l'ecriture, et « Annuler » doit pouvoir reecrire ce qu'il a lu.
 */
function valeurSurMesure(valeur: unknown): CustomFieldValue | undefined {
  if (
    valeur === null ||
    typeof valeur === 'string' ||
    typeof valeur === 'number' ||
    typeof valeur === 'boolean'
  ) {
    return valeur;
  }
  if (Array.isArray(valeur) && valeur.every((item) => typeof item === 'string')) {
    return valeur as string[];
  }
  return undefined;
}

/** L'instant de debut, seulement s'il en est vraiment un. */
export function debutDeFiche(fiche: Record<string, unknown>): Date | null {
  const brut = fiche['startAt'];
  if (!(brut instanceof Date) || Number.isNaN(brut.getTime())) return null;
  return brut;
}

/** Le `personId` de la personne a qui le creneau appartient (une adresse sur une entree d'avant l'annuaire). */
export function personneDeFiche(fiche: Record<string, unknown>): string {
  return texte(fiche['workerId']) ?? '';
}

/**
 * L'ETAT D'AVANT, pour « Annuler ». Le sac est recopie ENTIER : le SDK fusionne
 * clef par clef, donc ne remettre que `notes` et les choix laisserait en place
 * une clef posee entre temps par une autre porte.
 */
export function photoDeFiche(fiche: Record<string, unknown>): PhotoDeFiche {
  const photo: PhotoDeFiche = {};
  const debut = debutDeFiche(fiche);
  if (debut !== null) photo.startAt = debut;
  const minutes = fiche['durationMinutes'];
  if (typeof minutes === 'number' && Number.isFinite(minutes)) {
    photo.durationMinutes = Math.round(minutes);
  }
  /* LE PROJET ET LA TACHE REVIENNENT A LEUR ETAT D'AVANT, absence comprise :
     `null` efface ce qu'un geste aurait pose entre-temps. */
  photo.projectId = texte(fiche['projectId']) ?? null;
  photo.taskId = texte(fiche['taskId']) ?? null;
  const sac: Record<string, CustomFieldValue> = {};
  for (const [clef, brute] of Object.entries(sacDe(fiche))) {
    const valeur = valeurSurMesure(brute);
    if (valeur !== undefined) sac[clef] = valeur;
  }
  photo.customFields = sac;
  return photo;
}

/**
 * LA MEME PHOTO, COMPLETEE DES CLEFS QUE LE GESTE VA ECRIRE.
 *
 * Le sac se fusionne CLEF PAR CLEF cote SDK : une clef que le geste vient de
 * poser et que la photo ne porte pas (elle n'existait pas avant) survivrait a
 * l'annulation. Les clefs citees reviennent donc explicitement a leur valeur
 * d'avant, ou a `null`, qui EFFACE la clef. Le `null` ne vaut QUE dans le sac :
 * sur un champ natif il serait refuse par le schema, et c'est tant mieux.
 */
export function photoAvecClefs(
  fiche: Record<string, unknown>,
  clefs: readonly string[],
): PhotoDeFiche {
  const photo = photoDeFiche(fiche);
  const sac: Record<string, CustomFieldValue> = { ...photo.customFields };
  const avant = sacDe(fiche);
  for (const clef of clefs) sac[clef] = valeurSurMesure(avant[clef]) ?? null;
  photo.customFields = sac;
  return photo;
}

/* ------------------------------------------------------------------ */
/* La semaine                                                          */
/* ------------------------------------------------------------------ */

/** Le lundi de la semaine d'une date, a minuit local. */
export function mondayOf(date: Date): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setDate(result.getDate() - (result.getDay() === 0 ? 6 : result.getDay() - 1));
  return result;
}

/** Le meme jour, decale de N jours. */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** La date pure `YYYY-MM-DD` d'un jour, jamais un instant UTC. */
export function weekKey(date: Date): string {
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Une date pure `YYYY-MM-DD` relue comme un jour LOCAL. */
export function dateFromKey(key: string): Date {
  const [year = 1970, month = 1, day = 1] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Le libelle de la semaine affichee, du lundi au vendredi ou au dimanche. */
export function weekLabel(monday: Date, weekend: boolean): string {
  const friday = addDays(monday, weekend ? 6 : 4);
  const formatMonth = (date: Date): string =>
    new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(date).replace('.', '');
  const startMonth = formatMonth(monday);
  const endMonth = formatMonth(friday);
  return startMonth === endMonth
    ? `${String(monday.getDate())} - ${String(friday.getDate())} ${endMonth}. ${String(friday.getFullYear())}`
    : `${String(monday.getDate())} ${startMonth}. - ${String(friday.getDate())} ${endMonth}. ${String(friday.getFullYear())}`;
}

/**
 * La colonne d'un instant dans la semaine, 0 = lundi. ARRONDI, et pas une
 * division entiere : un jour de changement d'heure dure 23 ou 25 heures.
 */
function indexDeJour(date: Date, monday: Date): number {
  const jour = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((jour.getTime() - monday.getTime()) / JOUR_MS);
}

/* ------------------------------------------------------------------ */
/* Fiche -> creneau, creneau -> fiche                                  */
/* ------------------------------------------------------------------ */

/**
 * UNE FICHE DU JOURNAL DEVIENT UN CRENEAU, ou rien.
 *
 * Rend `null` quand la fiche n'a pas d'instant lisible, quand elle tombe hors
 * de la semaine affichee, ou quand elle n'a pas d'id : aucun de ces trois cas
 * n'est une faute de la fiche, seulement une fiche qui n'a rien a faire sur
 * cette grille-ci.
 *
 * UNE DUREE ABSENTE VAUT UN PAS, pas zero : un creneau de hauteur nulle serait
 * invisible ET inaccessible, donc impossible a corriger a l'ecran.
 */
/** Ce qu'il faut pour titrer un creneau : le champ, ses choix, son mot d'absence. */
export interface TitreDeCreneau {
  key: string;
  options: readonly { value: string; label: string }[];
  vide: string;
}

/** Le titre d'un creneau : le mot du choix, le texte tel quel, ou le mot d'absence. */
export function titreDuCreneau(sac: Record<string, unknown>, titre: TitreDeCreneau | null): string {
  if (titre === null) return 'Saisie';
  const valeur = texte(sac[titre.key]);
  if (valeur === undefined) return titre.vide;
  return titre.options.find((option) => option.value === valeur)?.label ?? valeur;
}

export function creneauDeFiche(
  fiche: Record<string, unknown>,
  monday: Date,
  titre: TitreDeCreneau | null = null,
): CreneauSemaine | null {
  const id = texte(fiche['id']);
  if (id === undefined) return null;
  const debut = debutDeFiche(fiche);
  if (debut === null) return null;
  const day = indexDeJour(debut, monday);
  if (day < 0 || day > 6) return null;

  const start = debut.getHours() + debut.getMinutes() / 60;
  const brut = fiche['durationMinutes'];
  const minutes =
    typeof brut === 'number' && Number.isFinite(brut) && brut > 0 ? brut : PAS_MINUTES;
  /* Coupe a minuit : la grille s'arrete a 24 h, et un creneau qui deborde se
     lirait sinon par-dessus le jour suivant. Seul l'AFFICHAGE est coupe, la
     fiche garde sa duree. */
  const duration = Math.max(PAS_MINUTES / 60, Math.min(minutes / 60, 24 - start));

  const sac = sacDe(fiche);
  const creneau: CreneauSemaine = {
    id,
    day,
    start,
    duration,
    title: titreDuCreneau(sac, titre),
    ownerId: personneDeFiche(fiche),
  };
  const projet = texte(fiche['projectId']);
  if (projet !== undefined) creneau.projectId = projet;
  const tache = texte(fiche['taskId']);
  if (tache !== undefined) creneau.taskId = tache;
  const note = texte(sac['notes']);
  if (note !== undefined) creneau.note = note;
  const choix: Record<string, string> = {};
  for (const [clef, valeur] of Object.entries(sac)) {
    const mot = texte(valeur);
    if (mot !== undefined) choix[clef] = mot;
  }
  if (Object.keys(choix).length > 0) creneau.choix = choix;
  return creneau;
}

/** Les creneaux de la semaine, dans l'ordre ou les fiches arrivent. */
export function creneauxDeLaSemaine(
  fiches: readonly Record<string, unknown>[],
  monday: Date,
  titre: TitreDeCreneau | null = null,
): CreneauSemaine[] {
  const creneaux: CreneauSemaine[] = [];
  for (const fiche of fiches) {
    const creneau = creneauDeFiche(fiche, monday, titre);
    if (creneau !== null) creneaux.push(creneau);
  }
  return creneaux;
}

/**
 * CRENEAU + SEMAINE -> INSTANT LOCAL. Les minutes sont calees sur le pas de
 * cinq : la grille les y cale deja, l'editeur aussi, mais c'est ici que la
 * garantie tient pour tout le monde.
 */
export function instantDuCreneau(monday: Date, day: number, start: number): Date {
  const jour = addDays(monday, day);
  const minutes = calerMinutes(start * 60);
  return new Date(
    jour.getFullYear(),
    jour.getMonth(),
    jour.getDate(),
    Math.floor(minutes / 60),
    minutes % 60,
    0,
    0,
  );
}

/** Un instant local a partir d'une date pure et d'une minute dans la journee. */
export function instantDuJour(jour: string, minutes: number): Date {
  const date = dateFromKey(jour);
  const cale = calerMinutes(minutes);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    Math.floor(cale / 60),
    cale % 60,
    0,
    0,
  );
}

/** Des minutes entieres, calees sur le pas, jamais en dessous d'un pas. */
export function calerMinutes(minutes: number): number {
  return Math.max(PAS_MINUTES, Math.round(minutes / PAS_MINUTES) * PAS_MINUTES);
}

/** Une duree en heures decimales, rendue en minutes entieres pour le SDK. */
export function minutesDeDuree(heures: number): number {
  return calerMinutes(heures * 60);
}

/* ------------------------------------------------------------------ */
/* Les libelles                                                        */
/* ------------------------------------------------------------------ */

/** Une quantite d'heures, telle qu'on l'annonce en tete de colonne. */
export function hoursLabel(value: number): string {
  const minutes = Math.round(value * 60);
  return minutes % 60 === 0
    ? `${String(minutes / 60)} h`
    : `${String(Math.floor(minutes / 60))} h ${String(minutes % 60).padStart(2, '0')}`;
}

/** Une heure de la journee en heures decimales, rendue `HH:MM`. */
export function timeLabel(value: number): string {
  const minutes = Math.round(value * 60);
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Les minutes d'une journee, rendues `HH:MM`. */
export function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Le nom court d'un jour : la tete de colonne de la grille. */
export const JOURS = ['Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.', 'Dim.'] as const;

/** Le nom entier d'un jour : une PHRASE se lit mal avec une abreviation. */
export const JOURS_LONGS = [
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche',
] as const;

/** Deux plages horaires se chevauchent-elles ? Bornes de fin exclues. */
export function overlaps(
  a: { start: number; duration: number },
  b: { start: number; duration: number },
): boolean {
  return a.start < b.start + b.duration && b.start < a.start + a.duration;
}
